import React from "react";
import usePlanningWriteActivity from "../hooks/usePlanningWriteActivity.js";
import PlanningWriteActivityNotice from "./PlanningWriteActivityNotice.jsx";
import { createPortal } from "react-dom";
import { AlertTriangle, Clock3, Pencil, Plus, Trash2, Wrench, X } from "lucide-react";
import { focusBillingDialogElement, handleBillingDialogKeyDown } from "../utils/billingDialogAccessibility.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { joinClasses } from "./ResultsPrimitives.jsx";

const EMPTY_DRAFT = Object.freeze({
  description: "",
  endTime: "",
  id: "",
  startTime: "",
});

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function timeToMinutes(value) {
  const match = /^(\d{2}):(\d{2})$/.exec(normalizeText(value));
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59
    ? hour * 60 + minute
    : null;
}

function formatIncidentTime(value) {
  const match = /^(\d{2}):(\d{2})$/.exec(normalizeText(value));
  return match ? `${Number(match[1])} h ${match[2]}` : normalizeText(value) || "--";
}

function formatUpdatedAt(value) {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) {
    return "Date inconnue";
  }
  const parsedValue = new Date(normalizedValue);
  if (Number.isNaN(parsedValue.getTime())) {
    return normalizedValue;
  }
  return new Intl.DateTimeFormat("fr-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsedValue);
}

function sortIncidents(incidents = []) {
  return (Array.isArray(incidents) ? incidents : [])
    .filter((incident) => incident && typeof incident === "object")
    .slice()
    .sort((left, right) => (
      (timeToMinutes(left?.start_time) ?? Number.MAX_SAFE_INTEGER)
      - (timeToMinutes(right?.start_time) ?? Number.MAX_SAFE_INTEGER)
      || normalizeText(left?.id).localeCompare(normalizeText(right?.id))
    ));
}

function buildIncidentCollectionSignature(incidents = []) {
  return JSON.stringify(sortIncidents(incidents).map((incident) => ({
    createdAt: normalizeText(incident?.created_at),
    description: normalizeText(incident?.description),
    displayEndMinutes: incident?.display_end_minutes ?? null,
    displayStartMinutes: incident?.display_start_minutes ?? null,
    endTime: normalizeText(incident?.end_time),
    id: normalizeText(incident?.id),
    startTime: normalizeText(incident?.start_time),
    updatedAt: normalizeText(incident?.updated_at),
    updatedBy: normalizeText(incident?.updated_by),
  })));
}

function findReturnedIncident(payload, incidentId = "") {
  if (payload?.truck_incident && typeof payload.truck_incident === "object") {
    return payload.truck_incident;
  }
  const resultIncidents = Array.isArray(payload?.result_payload?.truck_incidents)
    ? payload.result_payload.truck_incidents
    : [];
  const safeIncidentId = normalizeText(incidentId);
  return safeIncidentId
    ? resultIncidents.find((incident) => normalizeText(incident?.id) === safeIncidentId) || null
    : resultIncidents[resultIncidents.length - 1] || null;
}

function resolveReturnedRuntimeRevision(payload, fallback = "") {
  return normalizeText(payload?.runtime_revision)
    || normalizeText(payload?.result_payload?.runtime_revision)
    || normalizeText(fallback);
}

function validateDraft(draft, incidents = []) {
  const description = normalizeText(draft?.description);
  const startMinutes = timeToMinutes(draft?.startTime);
  const endMinutes = timeToMinutes(draft?.endTime);
  if (startMinutes == null || endMinutes == null) {
    return "Saisissez une heure de début et une heure de fin à la minute près.";
  }
  if (endMinutes <= startMinutes) {
    return "L'heure de fin doit être après l'heure de début.";
  }
  if (!description) {
    return "La description est obligatoire.";
  }
  if (description.length > 1000) {
    return "La description ne peut pas dépasser 1 000 caractères.";
  }
  const overlaps = sortIncidents(incidents).some((incident) => {
    if (normalizeText(incident?.id) === normalizeText(draft?.id)) {
      return false;
    }
    const existingStart = timeToMinutes(incident?.start_time);
    const existingEnd = timeToMinutes(incident?.end_time);
    return existingStart != null
      && existingEnd != null
      && startMinutes < existingEnd
      && endMinutes > existingStart;
  });
  return overlaps
    ? "Cette plage chevauche déjà un bris mécanique ou contretemps pour ce camion."
    : "";
}

function IncidentEditor({
  draft,
  errorMessage = "",
  incidents = [],
  isPending = false,
  onCancel,
  onChange,
  onSubmit,
}) {
  const validationMessage = validateDraft(draft, incidents);
  const descriptionLength = String(draft?.description || "").length;
  const isEditing = Boolean(draft?.id);
  const planningWriteMessage = usePlanningWriteActivity();

  return (
    <form
      className="grid gap-2 rounded-[4px] border border-[var(--color-border-success-subtle)] bg-accent-soft/55 p-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (!validationMessage && !isPending && !planningWriteMessage) {
          onSubmit?.();
        }
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="m-0 text-[0.64rem] font-black uppercase tracking-[0.14em] text-[var(--color-shell-action-soft-ink)]">Plage civile</p>
          <h3 className="m-0 mt-0.5 text-[0.95rem] font-semibold text-slate-950">
            {isEditing ? "Modifier l'incident" : "Nouvel incident"}
          </h3>
        </div>
        <button
          type="button"
          className="rounded-[4px] border border-slate-300 bg-white px-2.5 py-1 text-[0.7rem] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={isPending}
          onClick={onCancel}
        >
          Annuler
        </button>
      </div>

      <div className="grid w-full max-w-[18rem] grid-cols-2 gap-2">
        <label className="grid gap-0.5 text-[0.7rem] font-semibold text-slate-700">
          <span>Début</span>
          <input
            type="time"
            step="60"
            required
            className="h-8 w-full min-w-0 rounded-[4px] border border-slate-200 bg-white px-2.5 text-[0.8rem] font-semibold text-slate-950 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
            disabled={isPending}
            value={draft.startTime}
            onChange={(event) => onChange?.({ ...draft, startTime: event.target.value })}
          />
        </label>
        <label className="grid gap-0.5 text-[0.7rem] font-semibold text-slate-700">
          <span>Fin</span>
          <input
            type="time"
            step="60"
            required
            className="h-8 w-full min-w-0 rounded-[4px] border border-slate-200 bg-white px-2.5 text-[0.8rem] font-semibold text-slate-950 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
            disabled={isPending}
            value={draft.endTime}
            onChange={(event) => onChange?.({ ...draft, endTime: event.target.value })}
          />
        </label>
      </div>

      <label className="grid gap-0.5 text-[0.7rem] font-semibold text-slate-700">
        <span className="flex items-center justify-between gap-2">
          <span>Description interne</span>
          <span className={descriptionLength > 1000 ? "text-red-700" : "text-slate-500"}>{descriptionLength}/1 000</span>
        </span>
        <textarea
          rows={2}
          maxLength={1000}
          required
          className="min-h-[3.25rem] max-h-[8rem] resize-y rounded-[4px] border border-slate-200 bg-white px-2.5 py-1.5 text-[0.8rem] leading-4.5 text-slate-950 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
          disabled={isPending}
          value={draft.description}
          onChange={(event) => onChange?.({ ...draft, description: event.target.value })}
        />
      </label>

      {errorMessage || validationMessage ? (
        <p className="m-0 flex items-start gap-2 rounded-[4px] border border-red-200 bg-red-50 px-2.5 py-1.5 text-[0.7rem] font-semibold leading-4 text-red-700" role="alert">
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{errorMessage || validationMessage}</span>
        </p>
      ) : null}

      {!isPending ? <PlanningWriteActivityNotice /> : null}
      <div className="flex justify-end">
        <button
          type="submit"
          className="inline-flex h-8 items-center justify-center rounded-[4px] border border-accent bg-accent px-3 text-[0.74rem] font-semibold text-white transition hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={Boolean(validationMessage) || isPending || Boolean(planningWriteMessage)}
        >
          {isPending ? "Enregistrement…" : isEditing ? "Enregistrer" : "Ajouter l'incident"}
        </button>
      </div>
    </form>
  );
}

export default function TruckIncidentsDialog({
  dispatchDate = "",
  dispatchPeriod = "day",
  incidents = [],
  isOpen = false,
  isReadOnly = false,
  onClose,
  onCreate,
  onDelete,
  onUpdate,
  readOnlyReason = "",
  runtimeRevision = "",
  truckId = "",
  truckLabel = "",
}) {
  const planningWriteMessage = usePlanningWriteActivity();
  const [draft, setDraft] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isPending, setIsPending] = React.useState(false);
  const [localIncidents, setLocalIncidents] = React.useState(() => sortIncidents(incidents));
  const [localRuntimeRevision, setLocalRuntimeRevision] = React.useState(runtimeRevision);
  const [pendingDeleteId, setPendingDeleteId] = React.useState("");
  const closeButtonRef = React.useRef(null);
  const dialogRef = React.useRef(null);
  const restoreFocusRef = React.useRef(null);
  const normalizedDispatchPeriod = normalizeText(dispatchPeriod).toLowerCase() === "evening" ? "evening" : "day";
  const periodLabel = normalizedDispatchPeriod === "evening" ? "Soir" : "Jour";
  const incomingIncidentsSignature = buildIncidentCollectionSignature(incidents);
  const incomingIncidentsSyncKey = [
    normalizeText(dispatchDate),
    normalizedDispatchPeriod,
    normalizeText(truckId),
    incomingIncidentsSignature,
  ].join("::");
  const lastSyncedIncomingIncidentsKeyRef = React.useRef(incomingIncidentsSyncKey);

  React.useEffect(() => {
    if (lastSyncedIncomingIncidentsKeyRef.current === incomingIncidentsSyncKey) {
      return;
    }
    lastSyncedIncomingIncidentsKeyRef.current = incomingIncidentsSyncKey;
    setLocalIncidents(sortIncidents(incidents));
  }, [incidents, incomingIncidentsSyncKey]);

  React.useEffect(() => {
    setLocalRuntimeRevision(normalizeText(runtimeRevision));
  }, [runtimeRevision]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    setDraft(null);
    setErrorMessage("");
    setPendingDeleteId("");
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, truckId, normalizedDispatchPeriod]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    restoreFocusRef.current = typeof document !== "undefined" ? document.activeElement : null;
    focusBillingDialogElement(closeButtonRef.current || dialogRef.current);
    return () => {
      focusBillingDialogElement(restoreFocusRef.current);
      restoreFocusRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const beginCreate = () => {
    setErrorMessage("");
    setPendingDeleteId("");
    setDraft({ ...EMPTY_DRAFT });
  };
  const beginEdit = (incident) => {
    setErrorMessage("");
    setPendingDeleteId("");
    setDraft({
      description: normalizeText(incident?.description),
      endTime: normalizeText(incident?.end_time),
      id: normalizeText(incident?.id),
      startTime: normalizeText(incident?.start_time),
    });
  };
  const submitDraft = async () => {
    const validationMessage = validateDraft(draft, localIncidents);
    if (!draft || validationMessage || isReadOnly || isPending || planningWriteMessage) {
      setErrorMessage(validationMessage);
      return;
    }
    setIsPending(true);
    setErrorMessage("");
    const commonPayload = {
      description: normalizeText(draft.description),
      dispatchDate,
      dispatchPeriod: normalizedDispatchPeriod,
      endTime: draft.endTime,
      expectedRuntimeRevision: localRuntimeRevision,
      startTime: draft.startTime,
      truckId,
      truckName: truckLabel,
    };
    try {
      const payload = draft.id
        ? await onUpdate?.({ ...commonPayload, incidentId: draft.id })
        : await onCreate?.(commonPayload);
      if (!payload) {
        return;
      }
      const returnedIncident = findReturnedIncident(payload, draft.id);
      if (returnedIncident) {
        setLocalIncidents((currentIncidents) => sortIncidents([
          ...currentIncidents.filter((incident) => normalizeText(incident?.id) !== normalizeText(returnedIncident?.id)),
          returnedIncident,
        ]));
      }
      setLocalRuntimeRevision((currentRevision) => resolveReturnedRuntimeRevision(payload, currentRevision));
      setDraft(null);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Impossible d'enregistrer cet incident.");
    } finally {
      setIsPending(false);
    }
  };
  const deleteIncident = async (incident) => {
    const incidentId = normalizeText(incident?.id);
    if (!incidentId || isReadOnly || isPending || planningWriteMessage) {
      return;
    }
    setIsPending(true);
    setErrorMessage("");
    try {
      const payload = await onDelete?.({
        dispatchDate,
        dispatchPeriod: normalizedDispatchPeriod,
        expectedRuntimeRevision: localRuntimeRevision,
        incidentId,
        truckId,
        truckName: truckLabel,
      });
      if (!payload) {
        return;
      }
      setLocalIncidents((currentIncidents) => currentIncidents.filter((item) => normalizeText(item?.id) !== incidentId));
      setLocalRuntimeRevision((currentRevision) => resolveReturnedRuntimeRevision(payload, currentRevision));
      setPendingDeleteId("");
      if (normalizeText(draft?.id) === incidentId) {
        setDraft(null);
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Impossible de supprimer cet incident.");
    } finally {
      setIsPending(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-modal isolate font-sans" data-truck-incidents-dialog="true" role="presentation">
      <div
        className="absolute inset-0 overflow-y-auto bg-slate-950/45 px-3 py-3 backdrop-blur-xs sm:px-5"
      >
        <div
          className="flex min-h-full items-center justify-center"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isPending) {
              onClose?.();
            }
          }}
        >
          <section
            aria-labelledby="truck-incidents-dialog-title"
            aria-modal="true"
            className="w-full max-w-[min(43rem,calc(100vw-1.5rem))] overflow-hidden rounded-[4px] border border-slate-200 bg-white text-slate-950 shadow-[0_28px_70px_rgba(15,23,42,0.26)]"
            onKeyDown={(event) => {
              if (event.key === "Escape" && isPending) {
                return;
              }
              handleBillingDialogKeyDown(event, {
                activeElement: document.activeElement,
                dialog: dialogRef.current,
                onClose,
              });
            }}
            ref={dialogRef}
            role="dialog"
            tabIndex={-1}
          >
            <div className="h-1 bg-accent" />
            <div className="max-h-[calc(100dvh-2rem)] overflow-y-auto px-4 pb-3 pt-2.5 sm:px-4">
              <div className="flex items-start justify-between gap-2.5 border-b border-slate-200 pb-2">
                <div className="flex min-w-0 items-start gap-2.5">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] border border-[var(--color-border-success-subtle)] bg-accent-soft text-[var(--color-shell-action-soft-ink)]">
                    <Wrench aria-hidden="true" className="h-4.5 w-4.5" strokeWidth={2.3} />
                  </span>
                  <div className="min-w-0">
                    <p className="m-0 text-[0.64rem] font-black uppercase tracking-[0.16em] text-[var(--color-shell-action-soft-ink)]">Bris mécanique / Contretemps</p>
                    <h2 id="truck-incidents-dialog-title" className="m-0 mt-0.5 truncate text-[1.1rem] font-semibold tracking-[-0.03em]">
                      {truckLabel || truckId || "Camion"}
                    </h2>
                    <p className="m-0 text-[0.7rem] font-medium text-slate-600">{dispatchDate || "Date courante"} · {periodLabel}</p>
                  </div>
                </div>
                <button
                  ref={closeButtonRef}
                  type="button"
                  aria-label="Fermer"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={isPending}
                  onClick={onClose}
                >
                  <X aria-hidden="true" className="h-4 w-4" />
                </button>
              </div>

              {isReadOnly ? (
                <div className="mt-2 flex items-start gap-2 rounded-[4px] border border-amber-200 bg-amber-50 px-2.5 py-2 text-[0.72rem] font-semibold leading-4.5 text-amber-800" role="status">
                  <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{readOnlyReason || "Ces incidents sont consultables en lecture seule."}</span>
                </div>
              ) : null}

              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="m-0 text-[0.74rem] font-semibold text-slate-600">
                  {localIncidents.length} incident{localIncidents.length > 1 ? "s" : ""} dans cette période
                </p>
                {!isReadOnly && !draft ? (
                  <button
                    type="button"
                    className="inline-flex h-8 items-center gap-1.5 rounded-[4px] border border-accent bg-accent px-3 text-[0.72rem] font-semibold text-white transition hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                    onClick={beginCreate}
                  >
                    <Plus aria-hidden="true" className="h-4 w-4" />
                    Ajouter
                  </button>
                ) : null}
              </div>

              {draft ? (
                <div className="mt-2">
                  <IncidentEditor
                    draft={draft}
                    errorMessage={errorMessage}
                    incidents={localIncidents}
                    isPending={isPending}
                    onCancel={() => {
                      setDraft(null);
                      setErrorMessage("");
                    }}
                    onChange={(nextDraft) => {
                      setDraft(nextDraft);
                      setErrorMessage("");
                    }}
                    onSubmit={submitDraft}
                  />
                </div>
              ) : errorMessage ? (
                <p className="m-0 mt-2 rounded-[4px] border border-red-200 bg-red-50 px-2.5 py-1.5 text-[0.7rem] font-semibold text-red-700" role="alert">{errorMessage}</p>
              ) : null}

              <div className="mt-2 grid gap-1.5">
                {localIncidents.length ? localIncidents.map((incident) => {
                  const incidentId = normalizeText(incident?.id);
                  const isDeleteConfirmationOpen = pendingDeleteId === incidentId;
                  return (
                    <article key={incidentId || `${incident?.start_time}-${incident?.end_time}`} className="rounded-[4px] border border-red-200 bg-[linear-gradient(135deg,rgba(254,242,242,0.96),rgba(255,255,255,0.98))] px-3 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-[0.9rem] font-black text-red-800">
                            <Clock3 aria-hidden="true" className="h-4 w-4 shrink-0" />
                            <span>{formatIncidentTime(incident?.start_time)} — {formatIncidentTime(incident?.end_time)}</span>
                          </div>
                          <p className="m-0 mt-1.5 whitespace-pre-wrap break-words text-[0.8rem] leading-5 text-slate-700">{normalizeText(incident?.description)}</p>
                          <p className="m-0 mt-2 text-[0.66rem] font-medium text-slate-500">
                            Dernière modification : {formatUpdatedAt(incident?.updated_at || incident?.created_at)}
                            {normalizeText(incident?.updated_by) ? ` · ${normalizeText(incident.updated_by)}` : ""}
                          </p>
                        </div>
                        {!isReadOnly ? (
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              type="button"
                              aria-label="Modifier l'incident"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={isPending}
                              onClick={() => beginEdit(incident)}
                            >
                              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              aria-label="Supprimer l'incident"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-200 bg-white text-red-700 transition hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={isPending}
                              onClick={() => setPendingDeleteId(isDeleteConfirmationOpen ? "" : incidentId)}
                            >
                              <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                      {isDeleteConfirmationOpen && !isPending ? <PlanningWriteActivityNotice /> : null}
                      {isDeleteConfirmationOpen ? (
                        <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-red-200 pt-2">
                          <span className="mr-auto text-[0.72rem] font-semibold text-red-800">Supprimer définitivement cette plage ?</span>
                          <button
                            type="button"
                            className="rounded-[4px] border border-slate-200 bg-white px-3 py-1.5 text-[0.72rem] font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isPending}
                            onClick={() => setPendingDeleteId("")}
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            className={joinClasses([
                              "rounded-[4px] border border-red-700 bg-red-700 px-3 py-1.5 text-[0.72rem] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2",
                              isPending ? "cursor-not-allowed opacity-50" : "hover:border-red-800 hover:bg-red-800",
                            ])}
                            disabled={isPending || Boolean(planningWriteMessage)}
                            onClick={() => void deleteIncident(incident)}
                          >
                            {isPending ? "Suppression…" : "Supprimer"}
                          </button>
                        </div>
                      ) : null}
                    </article>
                  );
                }) : draft ? null : (
                  <div className="rounded-[4px] border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-center">
                    <p className="m-0 text-[0.86rem] font-semibold text-slate-800">Aucun bris mécanique ou contretemps</p>
                    <p className="m-0 mt-1 text-[0.74rem] leading-5 text-slate-500">La tournée conserve actuellement ses horaires projetés sans délai d'incident.</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>,
    getReactPortalRoot(),
  );
}
